import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Card } from "../../business/Card";

export default function ScrollCarousel({ items, type }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const cardWidth = 360 + 24; // width + gap

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;

    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
  };

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollAmount =
      direction === "left"
        ? el.scrollLeft - cardWidth
        : el.scrollLeft + cardWidth;

    el.scrollTo({ left: scrollAmount, behavior: "smooth" });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateScrollButtons);
    updateScrollButtons(); // initialize on mount

    return () => el.removeEventListener("scroll", updateScrollButtons);
  }, []);

  return (
    <div className="flex flex-col gap-6">
      {/* Arrows */}
      <div className="flex justify-end items-center gap-5">
        <button
          onClick={() => scroll("left")}
          disabled={!canScrollLeft}
          className={`p-2 rounded-full border backdrop-blur-md transition
            ${
              canScrollLeft
                ? "bg-white border-[#A3A3A3]"
                : "bg-gray-100 border-gray-300 cursor-not-allowed"
            }`}
        >
          <ChevronLeft
            className={`w-6 h-6 ${
              canScrollLeft ? "text-black" : "text-gray-400"
            }`}
          />
        </button>

        <button
          onClick={() => scroll("right")}
          disabled={!canScrollRight}
          className={`p-2 rounded-full border backdrop-blur-md transition
            ${
              canScrollRight
                ? "bg-white border-[#A3A3A3]"
                : "bg-gray-100 border-gray-300 cursor-not-allowed"
            }`}
        >
          <ChevronRight
            className={`w-6 h-6 ${
              canScrollRight ? "text-black" : "text-gray-400"
            }`}
          />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="relative w-full">
        <motion.div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto no-scrollbar scroll-smooth px-12"
          whileTap={{ cursor: "grabbing" }}
        >
          {items.map((item, index) => (
            <Card
              key={index}
              title={item.title}
              description={item.description}
              rating={item.rating}
              views={item.views}
              type={type}
              url={item.url}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
}
